export type OperatingCostMonthEntry = {
  key: string;
  value: number;
};

export type ParsedOperatingCostMonthlyCosts = {
  monthlyValues: Record<string, number>;
  total: number;
  frequency?: string;
};

function lshRoundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function lshIsMonthEntryArray(
  value: unknown,
): value is OperatingCostMonthEntry[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'key' in (value[0] as object)
  );
}

function lshSumMonthlyValues(monthlyValues: Record<string, number>): number {
  return lshRoundMoney(
    Object.values(monthlyValues).reduce(
      (sum, value) => sum + (Number(value) || 0),
      0,
    ),
  );
}

export function lshParseOperatingCostMonthlyCosts(
  raw: string | null | undefined,
): ParsedOperatingCostMonthlyCosts {
  const empty: ParsedOperatingCostMonthlyCosts = {monthlyValues: {}, total: 0};

  if (!raw || !String(raw).trim()) {
    return empty;
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') {
      return empty;
    }

    const monthlyValues: Record<string, number> = {};

    if (lshIsMonthEntryArray(parsed.months)) {
      parsed.months.forEach((entry) => {
        const key = String(entry?.key ?? '').toLowerCase();
        if (!key) return;
        monthlyValues[key] = lshRoundMoney(Number(entry?.value) || 0);
      });

      const explicitTotal = Number(parsed.total);
      const total =
        Number.isFinite(explicitTotal) && explicitTotal > 0
          ? lshRoundMoney(explicitTotal)
          : lshSumMonthlyValues(monthlyValues);

      return {
        monthlyValues,
        total,
        frequency:
          typeof parsed.frequency === 'string' ? parsed.frequency : undefined,
      };
    }

    if (
      parsed.monthlyValues &&
      typeof parsed.monthlyValues === 'object' &&
      !Array.isArray(parsed.monthlyValues)
    ) {
      Object.entries(parsed.monthlyValues as Record<string, unknown>).forEach(
        ([key, value]) => {
          const normalisedKey = String(key).toLowerCase();
          if (!normalisedKey) return;
          monthlyValues[normalisedKey] = lshRoundMoney(Number(value) || 0);
        },
      );

      if (Object.values(monthlyValues).some((value) => value > 0)) {
        const explicitTotal = Number(parsed.total);
        const total =
          Number.isFinite(explicitTotal) && explicitTotal > 0
            ? lshRoundMoney(explicitTotal)
            : lshSumMonthlyValues(monthlyValues);

        return {
          monthlyValues,
          total,
          frequency:
            typeof parsed.frequency === 'string' ? parsed.frequency : undefined,
        };
      }
    }

    const legacyTotal = Number(parsed.total);
    return {
      monthlyValues: {},
      total: Number.isFinite(legacyTotal) ? lshRoundMoney(legacyTotal) : 0,
      frequency:
        typeof parsed.frequency === 'string' ? parsed.frequency : undefined,
    };
  } catch {
    return empty;
  }
}

export function lshSerializeOperatingCostMonthlyCosts(
  monthlyValues: Record<string, number>,
  frequency = 'monthly',
): string {
  const months: OperatingCostMonthEntry[] = Object.entries(monthlyValues)
    .map(([key, value]) => ({
      key: key.toLowerCase(),
      value: lshRoundMoney(Number(value) || 0),
    }))
    .filter((entry) => entry.value !== 0);

  const total = lshSumMonthlyValues(
    Object.fromEntries(months.map((entry) => [entry.key, entry.value])),
  );

  return JSON.stringify({
    frequency,
    months,
    total,
  });
}

export function lshGetOperatingCostTotalFromMonthlyCosts(
  raw: string | null | undefined,
): number {
  return lshParseOperatingCostMonthlyCosts(raw).total;
}
